import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  HomeWorth,
  YearlyIncomeAndSavings,
  RetirementSavings,
  TimeFrame,
  YesNoNotSure,
  EmploymentStatus,
  LifeInsurancePossessed,
  LivingSituation,
  DisabilityInsurancePossessed,
} from '../types';
import { ActionItem } from '../types/FpuActionItem';

export interface CoverageCheckupState {
  currentStepIndex: number;
  married: YesNoNotSure;
  carOwned: YesNoNotSure;
  autoInsurancePossessed: YesNoNotSure;
  livingSituation: LivingSituation;
  healthInsurancePossessed: YesNoNotSure;
  lifeInsurancePossessed: YesNoNotSure;
  disabilityInsurancePossessed: YesNoNotSure;
  typeOfDisabilityInsurancePossessed: DisabilityInsurancePossessed;
  umbrellaInsurancePossessed: YesNoNotSure;
  identityTheftProtectionPossessed: YesNoNotSure;
  willPossessed: YesNoNotSure;
  willPurchasedInSameStateAsResidence: YesNoNotSure;
  dependents: YesNoNotSure;
  hdhpPossessed: YesNoNotSure;
  homeownersInsurancePossessed: YesNoNotSure;
  willReflectsCurrentWishes: YesNoNotSure;
  longTermCareInsurancePossessed: YesNoNotSure;
  age: string;
  autoInsuranceLastReviewed: TimeFrame;
  firstName: string;
  lastName: string;
  email: string;
  creditReportLastReviewed: TimeFrame;
  employmentStatus: EmploymentStatus;
  disabilityInsuranceLastReviewed: TimeFrame;
  yearlyHouseholdIncome: YearlyIncomeAndSavings;
  nonRetirementSavings: YearlyIncomeAndSavings;
  retirementSavings: RetirementSavings;
  healthInsuranceLastReviewed: TimeFrame;
  homeValue: HomeWorth;
  mortgageBalance: HomeWorth;
  homeownersInsuranceLastReviewed: TimeFrame;
  identityTheftProtectionLastReviewed: TimeFrame;
  typeOfLifeInsurancePossessed: LifeInsurancePossessed;
  lifeInsuranceLastReviewed: TimeFrame;
  cancerInsurancePossessed: boolean;
  accidentalDeathInsurancePossessed: boolean;
  accidentOrSicknessInsurancePossessed: boolean;
  cellPhoneInsurancePossessed: boolean;
  homeWarrantyInsurancePossessed: boolean;
  mortgageLifeInsurancePossessed: boolean;
  petInsurancePossessed: boolean;
  noneOfThese: boolean;
  umbrellaInsuranceLastReviewed: TimeFrame;
  guid: string;
  dateOfBirth?: string;
  userId?: string;
  completeFpuActionItemFunction: Function;
  actionItem: ActionItem | {};
}

const initialState: CoverageCheckupState = {
  currentStepIndex: 0,
  carOwned: '',
  autoInsurancePossessed: '',
  livingSituation: '',
  healthInsurancePossessed: '',
  lifeInsurancePossessed: '',
  disabilityInsurancePossessed: '',
  umbrellaInsurancePossessed: '',
  identityTheftProtectionPossessed: '',
  willPossessed: '',
  typeOfDisabilityInsurancePossessed: '',
  willPurchasedInSameStateAsResidence: '',
  married: '',
  dependents: '',
  hdhpPossessed: '',
  homeownersInsurancePossessed: '',
  willReflectsCurrentWishes: '',
  longTermCareInsurancePossessed: '',
  age: '',
  autoInsuranceLastReviewed: '',
  firstName: '',
  lastName: '',
  email: '',
  creditReportLastReviewed: '',
  employmentStatus: '',
  disabilityInsuranceLastReviewed: '',
  yearlyHouseholdIncome: '',
  nonRetirementSavings: '',
  retirementSavings: '',
  healthInsuranceLastReviewed: '',
  homeValue: '',
  mortgageBalance: '',
  homeownersInsuranceLastReviewed: '',
  identityTheftProtectionLastReviewed: '',
  typeOfLifeInsurancePossessed: '',
  lifeInsuranceLastReviewed: '',
  cancerInsurancePossessed: false,
  accidentalDeathInsurancePossessed: false,
  accidentOrSicknessInsurancePossessed: false,
  cellPhoneInsurancePossessed: false,
  homeWarrantyInsurancePossessed: false,
  mortgageLifeInsurancePossessed: false,
  petInsurancePossessed: false,
  noneOfThese: false,
  umbrellaInsuranceLastReviewed: '',
  guid: '',
  dateOfBirth: '',
  userId: '',
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  completeFpuActionItemFunction: () => {},
  actionItem: {},
};

const stepSlice = createSlice({
  name: 'stepSlice',
  initialState,
  reducers: {
    updateCoverageCheckupState: (
      appState,
      action: PayloadAction<Partial<CoverageCheckupState>>
    ) => ({
      ...appState,
      ...action.payload,
    }),
    goToNextStep: (appState) => {
      const newState = appState.currentStepIndex + 1;

      return {
        ...appState,
        currentStepIndex: newState,
      };
    },
    goToPreviousStep: (appState) => {
      if (appState.currentStepIndex > 0) {
        const newState = appState.currentStepIndex - 1;

        return {
          ...appState,
          currentStepIndex: newState,
        };
      }
      return appState;
    },
    resetState: () => initialState,
  },
});

const {
  updateCoverageCheckupState,
  goToPreviousStep,
  goToNextStep,
  resetState,
} = stepSlice.actions;
const stepReducer = stepSlice.reducer;

export {
  updateCoverageCheckupState,
  stepReducer,
  goToPreviousStep,
  goToNextStep,
  resetState,
};
